import type { ChangeEvent } from "react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { PiMagnifyingGlass } from "react-icons/pi"

import type { InputGroupProps } from "@chakra-ui/react"
import {
  CloseButton,
  forwardRef,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useMergeRefs,
} from "@chakra-ui/react"

import { clearDomInput } from "@supliful/common"

export type SearchInputProps = React.ComponentProps<typeof Input> & {
  controlled?: boolean
  wrapperProps?: InputGroupProps
  placeholder?: string
  onClear?: () => void
}

export const SearchInput = forwardRef(
  (
    {
      controlled,
      placeholder,
      onClear,
      value,
      wrapperProps,
      onChange,
      ...otherProps
    }: SearchInputProps,
    forwardedRef,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const ref = useMergeRefs(inputRef, forwardedRef)

    // Drives the clear button visibility for both controlled and uncontrolled
    // modes. Reading inputRef.current?.value at render is unreliable on the
    // first render (ref is null), so the indicator wouldn't appear when the
    // input mounts with a non-empty value (e.g. opening a URL with ?search=protein).
    const [hasValue, setHasValue] = useState<boolean>(() =>
      controlled ? Boolean(value) : Boolean(otherProps.defaultValue),
    )

    useEffect(() => {
      if (controlled) {
        setHasValue(Boolean(value))
      }
    }, [controlled, value])

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        if (!controlled) {
          setHasValue(Boolean(event.target.value))
        }
        onChange?.(event)
      },
      [controlled, onChange],
    )

    const clearInput = useCallback(() => {
      if (!controlled) {
        clearDomInput(inputRef.current)
        setHasValue(false)
      }

      if (onClear) {
        onClear()
      }
    }, [controlled, onClear])

    useEffect(() => {
      if (!value && controlled) {
        clearDomInput(inputRef.current)
      }
    }, [value, controlled, inputRef])

    return (
      <InputGroup
        alignItems="center"
        borderRadius="lg"
        flexGrow={2}
        background="white"
        border="none"
        _focus={{
          borderColor: "primary",
        }}
        {...wrapperProps}
      >
        <InputLeftElement pointerEvents="none" h="full">
          <Icon as={PiMagnifyingGlass} fontSize={20} color="neutrals.600" />
        </InputLeftElement>

        <Input
          width="full"
          height="2.5rem"
          flexGrow={2}
          border="1px solid"
          borderColor="neutrals.300"
          borderRadius="lg"
          ref={ref}
          type="text"
          placeholder={placeholder || "Search"}
          py={{ base: 2, lg: 3 }}
          pl={10}
          pr={10}
          fontSize={14}
          {...(controlled && {
            value,
          })}
          {...otherProps}
          onChange={handleChange}
        />

        <InputRightElement h="full" mr={2}>
          <CloseButton
            title="Clear input"
            size={{ base: "sm", md: "md" }}
            onClick={clearInput}
            isDisabled={!hasValue}
            display={!hasValue ? "none" : "flex"}
          />
        </InputRightElement>
      </InputGroup>
    )
  },
)
